import inheritedFromPhrase from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/inheritedFromPhrase';
import type UsageSettingView from '@UI/Store/AiConfiguration/Views/UsageSettingView';
import type { TemperatureBounds } from '@UI/Store/AiConfiguration/Views/UsageSettingsView';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { InputNumber } from '@verclam/ui-kit';

const conflictBlockStyle = css`
	margin: 0;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

type Props = {
	readonly setting: UsageSettingView<number, TemperatureBounds>;
	readonly value: number | null;
	readonly onValueChange: (value: number | null) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
	readonly isSubmitting: boolean;
};

/**
 * No "Personnaliser" switch: `temperature.editable` is only ever true at the account level (plan
 * §5.1), where there is nothing above to inherit from — the writable branch below is only ever
 * reached there.
 */
const TemperatureSettingField: React.FC<Props> = ({
	setting,
	value,
	onValueChange,
	errorMessage,
	isFormFieldValid,
	isSubmitting,
}) => {
	if (!setting.applicable) {
		return null;
	}

	if (!setting.editable) {
		return (
			<InputNumber
				name="temperature"
				label="Température"
				value={setting.effective}
				readOnly
				min={setting.bounds.minimum}
				max={setting.bounds.maximum}
				step={0.1}
				subLabel={inheritedFromPhrase(setting.effectiveLevel) ?? 'Aucun réglage d’appel.'}
			/>
		);
	}

	if (!setting.supported) {
		return (
			<React.Fragment>
				<InputNumber
					name="temperature"
					label="Température"
					value={setting.own}
					disabled
					min={setting.bounds.minimum}
					max={setting.bounds.maximum}
					step={0.1}
					subLabel={setting.unsupportedReason ?? undefined}
				/>
				{setting.isInConflict ? (
					<p css={conflictBlockStyle}>
						Ce réglage est enregistré mais le moteur affecté ne l’accepte pas. Il ne sera pas
						envoyé.
					</p>
				) : null}
			</React.Fragment>
		);
	}

	return (
		<InputNumber
			name="temperature"
			label="Température"
			value={value}
			onChange={onValueChange}
			disabled={isSubmitting}
			min={setting.bounds.minimum}
			max={setting.bounds.maximum}
			step={0.1}
			errorMessage={errorMessage}
			isFormFieldValid={isFormFieldValid}
		/>
	);
};

export default TemperatureSettingField;
